/** Per-bot scratch memory (lives outside the World; deterministic given the bot's seed). */
import type { BotSkill, EncounterId, Ms, Rng, UnitId, Vec2 } from '@keystone/sim';
import { profileFor, type BotSkillProfile } from './skill';

export interface Roll {
  /** Uniform value drawn once (e.g. kick timing within 30–70%), or a delay in ms. */
  at: number;
  /** Did the skill check pass (kick / dodge reliability). */
  ok: boolean;
  /** When the roll was drawn (stale rolls are dropped). */
  t: Ms;
}

export interface BotMemory {
  readonly rng: Rng;
  /** The skill in effect this think (`baseSkill`, or its learned form on a boss we wiped to). */
  skill: BotSkillProfile;
  /** The bot's own skill dial. */
  readonly baseSkill: BotSkillProfile;
  /** Wipe-loop safety (skill.ts learnedSkill): wipes per boss, the boss last engaged, wipes seen. */
  learn: { wipes: Map<EncounterId, number>; encounterId: EncounterId | null; wipesSeen: number; applied: number };
  /** One roll per observed thing (cast, telegraph), keyed by a stable id. */
  rolls: Map<string, Roll>;
  /** When a condition first became true, and when it was last seen true. */
  noticed: Map<string, { first: Ms; last: Ms }>;
  lastMoveDest: Vec2 | null;
  pullWaitSince: Ms | null;
  /** +patrols: a roaming mob the tank decided to pull before the route pack (patrols.ts). */
  pullOverride: UnitId | null;
  /** +patrols: since when the tank has been waiting for a patrol to walk away from the pull. */
  roamWaitSince: Ms | null;
  /** End time of the latest noticed enemy group pulse (healer schedules its group heal after it). */
  pulseLandsAt: Ms | null;
  /** Earliest time of the next rotation press (GCD slack; skill dial). */
  nextPressAt: Ms;
  /** The GCD/cast end the current slack was drawn for. */
  slackFor: Ms;
  /** Current sim time (set every think; rolls are stamped with it). */
  now: Ms;
  /** Wall-stuck detection (brain.ts planUnstick). */
  stuck: { since: Ms; at: Vec2; attempt: number; detourUntil: Ms };
}

export function createMemory(rng: Rng, skill: BotSkill): BotMemory {
  const prof = profileFor(skill);
  return { rng, skill: prof, baseSkill: prof, learn: { wipes: new Map(), encounterId: null, wipesSeen: 0, applied: 0 }, rolls: new Map(), noticed: new Map(), lastMoveDest: null, pullWaitSince: null, pullOverride: null, roamWaitSince: null, pulseLandsAt: null, nextPressAt: 0, slackFor: -1, now: 0, stuck: { since: 0, at: { x: 0, y: 0 }, attempt: 0, detourUntil: 0 } };
}

/** Draw (once) a roll for `key`: `at` uniform in [lo, hi), ok with probability p. */
export function rollFor(mem: BotMemory, key: string, p: number, lo = 0, hi = 1): Roll {
  let r = mem.rolls.get(key);
  if (!r) {
    r = { at: mem.rng.range(lo, hi), ok: mem.rng.chance(p), t: mem.now };
    mem.rolls.set(key, r);
  }
  return r;
}

/**
 * Per-stimulus reaction delay (skill dial): reactionMs plus a long-tailed jitter, drawn once per
 * key. `ok` false = this instance is never handled (missChance).
 */
export function reactionRoll(mem: BotMemory, key: string): Roll {
  const k = `rx:${key}`;
  let r = mem.rolls.get(k);
  if (!r) {
    const mean = mem.skill.reactionJitterMs;
    const u = mem.rng.next();
    const jitter = mean > 0 ? Math.min(3 * mean, -mean * Math.log(1 - u * 0.999)) : 0;
    r = { at: mem.skill.reactionMs + jitter, ok: !mem.rng.chance(mem.skill.missChance), t: mem.now };
    mem.rolls.set(k, r);
  }
  return r;
}

/** Reaction delay for one stimulus (ms after it appeared). */
export function reactionMsFor(mem: BotMemory, key: string): number {
  return reactionRoll(mem, key).at;
}

/** True once `cond` has held continuously for the bot's reaction time. */
export function noticed(mem: BotMemory, key: string, cond: boolean, now: Ms): boolean {
  if (!cond) {
    mem.noticed.delete(key);
    return false;
  }
  const prev = mem.noticed.get(key);
  // A condition not checked for a while (bot was busy elsewhere) is still remembered.
  const first = prev && now - prev.last <= NOTICE_MEMORY_MS ? prev.first : now;
  mem.noticed.set(key, { first, last: now });
  return now - first >= mem.skill.reactionMs;
}

/** How long an unchecked condition stays remembered. */
const NOTICE_MEMORY_MS: Ms = 3000;
/** Rolls older than this that no longer refer to a live thing are dropped. */
const ROLL_TTL_MS: Ms = 30_000;

/** Drop stale per-think state. `liveRollKeys` are rolls still referring to live things. */
export function endThink(mem: BotMemory, liveRollKeys: ReadonlySet<string>, now: Ms): void {
  for (const [k, v] of [...mem.noticed]) if (now - v.last > NOTICE_MEMORY_MS) mem.noticed.delete(k);
  if (mem.rolls.size > 256) for (const [k, r] of [...mem.rolls]) if (now - r.t > ROLL_TTL_MS && !liveRollKeys.has(k)) mem.rolls.delete(k);
}
